import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Tooltip } from '../components/ui/Tooltip';
import { ArrowLeft, Loader2, Plus, Trash2, Upload } from 'lucide-react';
import { fetchRevisionConfig, updateRevisionConfig, deployRevision, createRevision } from '../services/revisionService';
import { fetchIntegrationDetails } from '../services/integrationService';
import { scheduleDeployment, cancelScheduledDeployment } from '../services/configurationService';
import { usePageTitle } from '../hooks/usePageTitle';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import { useToast } from '../components/ui/Toast';
import DeployConfigurationModal from '../components/DeployConfigurationModal.jsx';
import TransformationSelector from '../components/TransformationSelector.jsx';
import logger from '../utils/logger';
import { toTitleCase } from '../utils/textUtils';

const CLASSIFICATIONS = [
  { value: 'sensitive', label: 'Sensitive' },
  { value: 'non_sensitive', label: 'Non-Sensitive' },
];

const DEFAULT_DESTINATION_FIELDS = [
  { value: 'Member ID', label: 'Member ID' },
  { value: 'Client Person ID', label: 'Client Person ID' },
  { value: 'Identifier', label: 'Identifier' },
  { value: 'Subscriber/policyholder ID', label: 'Subscriber/policyholder ID' },
  { value: 'Group/employer identifier', label: 'Group/employer identifier' },
  { value: 'Source person ID', label: 'Source person ID' },
  { value: 'Subscriber Family ID', label: 'Subscriber Family ID' },
  { value: 'DEID_PAT_ID', label: 'DEID_PAT_ID' },
  { value: 'DEID_SUB_FAMILY_ID', label: 'DEID_SUB_FAMILY_ID' },
  { value: 'DEID_CLM_ID', label: 'DEID_CLM_ID' },
  { value: 'DEID_PAT_FIRST_NAME', label: 'DEID_PAT_FIRST_NAME' },
  { value: 'CLM_ID', label: 'CLM_ID' },
  { value: 'PAT_ID', label: 'PAT_ID' },
  { value: 'PAT_FIRST_NAME', label: 'PAT_FIRST_NAME' },
  { value: 'PAT_LAST_NAME', label: 'PAT_LAST_NAME' },
  { value: 'PAT_MIDDLE_NAME', label: 'PAT_MIDDLE_NAME' },
  { value: 'PAT_BIRTH_DT', label: 'PAT_BIRTH_DT' },
  { value: 'PAT_GENDER', label: 'PAT_GENDER' },
  { value: 'PAT_SSN', label: 'PAT_SSN' },
  { value: 'PAT_HOME_ADDRESS_1', label: 'PAT_HOME_ADDRESS_1' },
  { value: 'PAT_HOME_ADDRESS_2', label: 'PAT_HOME_ADDRESS_2' },
  { value: 'PAT_HOME_ZIP', label: 'PAT_HOME_ZIP' },
  { value: 'SUB_FAMILY_ID', label: 'SUB_FAMILY_ID' },
  { value: 'SUB_ID', label: 'SUB_ID' },
  { value: 'SUB_FIRST_NAME', label: 'SUB_FIRST_NAME' },
  { value: 'SUB_LAST_NAME', label: 'SUB_LAST_NAME' },
  { value: 'SUB_MIDDLE_NAME', label: 'SUB_MIDDLE_NAME' },
  { value: 'SUB_BIRTH_DT', label: 'SUB_BIRTH_DT' },
  { value: 'SUB_SSN', label: 'SUB_SSN' },
  { value: 'CLIENT_PERSON_ID', label: 'CLIENT_PERSON_ID' },
  { value: 'SRC_PERSON_ID', label: 'SRC_PERSON_ID' },
  { value: 'DATA_SUPPLIER', label: 'DATA_SUPPLIER' },
  { value: 'TPA_ID', label: 'TPA_ID' },
  { value: 'CLM_SEQUENCE_NUMBER', label: 'CLM_SEQUENCE_NUMBER' },
  { value: 'CLM_STATUS', label: 'CLM_STATUS' },
  { value: 'CLM_STATUS_SRC', label: 'CLM_STATUS_SRC' },
  { value: 'CLM_STATUS_SRC_DESC', label: 'CLM_STATUS_SRC_DESC' },
  { value: 'RECEIVED_DT', label: 'RECEIVED_DT' },
  { value: 'TRANSACTION_DT', label: 'TRANSACTION_DT' },
  { value: 'PAID_DT', label: 'PAID_DT' },
  { value: 'CLM_COVERAGE_TYPE', label: 'CLM_COVERAGE_TYPE' },
  { value: 'ADMISSION_IND_SRC', label: 'ADMISSION_IND_SRC' },
  { value: 'LENGTH_OF_STAYS_DAYS_SRC', label: 'LENGTH_OF_STAYS_DAYS_SRC' },
  { value: 'ADMISSION_DT', label: 'ADMISSION_DT' },
  { value: 'DISCHARGE_DT', label: 'DISCHARGE_DT' },
  { value: 'DISCHARGE_STATUS_CD', label: 'DISCHARGE_STATUS_CD' },
  { value: 'PAT_STAY_CAT', label: 'PAT_STAY_CAT' },
  { value: 'CLM_TYPE', label: 'CLM_TYPE' },
  { value: 'IN_NETWORK_IND', label: 'IN_NETWORK_IND' },
  { value: 'GRP_ID_SRC', label: 'GRP_ID_SRC' },
  { value: 'GRP_EMPLOYER_ID', label: 'GRP_EMPLOYER_ID' },
  { value: 'GRP_HIERARCHY_1', label: 'GRP_HIERARCHY_1' },
  { value: 'GRP_HIERARCHY_1_DESC', label: 'GRP_HIERARCHY_1_DESC' },
  { value: 'GRP_HIERARCHY_2', label: 'GRP_HIERARCHY_2' },
  { value: 'GRP_HIERARCHY_2_DESC', label: 'GRP_HIERARCHY_2_DESC' },
  { value: 'GRP_PLAN_ID', label: 'GRP_PLAN_ID' },
  { value: 'GRP_PLAN_ID_DESC', label: 'GRP_PLAN_ID_DESC' },
  { value: 'SRV_LINE_KEY', label: 'SRV_LINE_KEY' },
  { value: 'SRV_START_DT', label: 'SRV_START_DT' },
  { value: 'SRV_END_DT', label: 'SRV_END_DT' },
  { value: 'SRV_LINE_NUMBER', label: 'SRV_LINE_NUMBER' },
  { value: 'SRV_UNITS', label: 'SRV_UNITS' },
  { value: 'PROC_CD', label: 'PROC_CD' },
  { value: 'PROC_CD_DESC', label: 'PROC_CD_DESC' },
  { value: 'PROC_CD_MODIFIER_1', label: 'PROC_CD_MODIFIER_1' },
  { value: 'PROC_CD_MODIFIER_1_DESC', label: 'PROC_CD_MODIFIER_1_DESC' },
  { value: 'PROC_CD_MODIFIER_2', label: 'PROC_CD_MODIFIER_2' },
  { value: 'PROC_CD_MODIFIER_2_DESC', label: 'PROC_CD_MODIFIER_2_DESC' },
  { value: 'PROC_CD_MODIFIER_3', label: 'PROC_CD_MODIFIER_3' },
  { value: 'PROC_CD_MODIFIER_3_DESC', label: 'PROC_CD_MODIFIER_3_DESC' },
  { value: 'PROC_CD_MODIFIER_4', label: 'PROC_CD_MODIFIER_4' },
  { value: 'PROC_CD_MODIFIER_4_DESC', label: 'PROC_CD_MODIFIER_4_DESC' },
  { value: 'PROC_CD_MODIFIERS', label: 'PROC_CD_MODIFIERS' },
  { value: 'DIAG_CDS', label: 'DIAG_CDS' },
  { value: 'REV_CD', label: 'REV_CD' },
  { value: 'REV_CD_DESC', label: 'REV_CD_DESC' },
  { value: 'DRG_CD', label: 'DRG_CD' },
  { value: 'DRG_CD_DESC', label: 'DRG_CD_DESC' },
  { value: 'PLACE_OF_SRV_CD', label: 'PLACE_OF_SRV_CD' },
  { value: 'PLACE_OF_SRV_CD_DESC', label: 'PLACE_OF_SRV_CD_DESC' },
  { value: 'BILL_TYPE', label: 'BILL_TYPE' },
  { value: 'BILL_TYPE_DESC', label: 'BILL_TYPE_DESC' },
  { value: 'TOTAL_CHARGE_AMT', label: 'TOTAL_CHARGE_AMT' },
  { value: 'ALLOWED_CHARGE_AMT_SRC', label: 'ALLOWED_CHARGE_AMT_SRC' },
  { value: 'COV_AMT', label: 'COV_AMT' },
  { value: 'PLAN_PAYMENT_AMT', label: 'PLAN_PAYMENT_AMT' },
  { value: 'DISCOUNT_AMT', label: 'DISCOUNT_AMT' },
  { value: 'DEDUCTIBLE_AMT', label: 'DEDUCTIBLE_AMT' },
  { value: 'COPAY_AMT', label: 'COPAY_AMT' },
  { value: 'COINSURANCE_AMT', label: 'COINSURANCE_AMT' },
  { value: 'OTHER_INSURANCE_AMT', label: 'OTHER_INSURANCE_AMT' },
  { value: 'DIAG_CD_1', label: 'DIAG_CD_1' },
  { value: 'DIAG_CD_1_DESC', label: 'DIAG_CD_1_DESC' },
  { value: 'DIAG_CD_2', label: 'DIAG_CD_2' },
  { value: 'DIAG_CD_2_DESC', label: 'DIAG_CD_2_DESC' },
  { value: 'DIAG_CD_3', label: 'DIAG_CD_3' },
  { value: 'DIAG_CD_3_DESC', label: 'DIAG_CD_3_DESC' },
  { value: 'DIAG_CD_4', label: 'DIAG_CD_4' },
  { value: 'DIAG_CD_4_DESC', label: 'DIAG_CD_4_DESC' },
  { value: 'DIAG_CD_5', label: 'DIAG_CD_5' },
  { value: 'DIAG_CD_5_DESC', label: 'DIAG_CD_5_DESC' },
  { value: 'PROV_NAME', label: 'PROV_NAME' },
  { value: 'PROV_SRC_ID', label: 'PROV_SRC_ID' },
  { value: 'PROV_TIN', label: 'PROV_TIN' },
  { value: 'PROV_TAXONOMY_CD', label: 'PROV_TAXONOMY_CD' },
  { value: 'PROV_NPI', label: 'PROV_NPI' },
  { value: 'PROV_CITY', label: 'PROV_CITY' },
  { value: 'PROV_STATE', label: 'PROV_STATE' },
  { value: 'PROV_ZIP', label: 'PROV_ZIP' },
  { value: 'REND_NAME', label: 'REND_NAME' },
  { value: 'REND_SRC_ID', label: 'REND_SRC_ID' },
  { value: 'REND_TAXONOMY_CD', label: 'REND_TAXONOMY_CD' },
  { value: 'REND_NPI', label: 'REND_NPI' },
  { value: 'REND_CITY', label: 'REND_CITY' },
  { value: 'REND_STATE', label: 'REND_STATE' },
  { value: 'REND_ZIP', label: 'REND_ZIP' },
  { value: 'REFER_NAME', label: 'REFER_NAME' },
  { value: 'REFER_SRC_ID', label: 'REFER_SRC_ID' },
  { value: 'REFER_TAXONOMY_CD', label: 'REFER_TAXONOMY_CD' },
  { value: 'REFER_NPI', label: 'REFER_NPI' },
  { value: 'REFER_CITY', label: 'REFER_CITY' },
  { value: 'REFER_STATE', label: 'REFER_STATE' },
  { value: 'REFER_ZIP', label: 'REFER_ZIP' },
  { value: 'TOOTH_CD', label: 'TOOTH_CD' },
  { value: 'TOOTH_CD_DESC', label: 'TOOTH_CD_DESC' },
  { value: 'SURFACE_CD', label: 'SURFACE_CD' },
  { value: 'SURFACE_CD_DESC', label: 'SURFACE_CD_DESC' },
  { value: 'REMARK_CD_1', label: 'REMARK_CD_1' },
  { value: 'REMARK_CD_1_DESC', label: 'REMARK_CD_1_DESC' },
  { value: 'REMARK_CD_2', label: 'REMARK_CD_2' },
  { value: 'REMARK_CD_2_DESC', label: 'REMARK_CD_2_DESC' },
  { value: 'REMARK_CD_3', label: 'REMARK_CD_3' },
  { value: 'REMARK_CD_3_DESC', label: 'REMARK_CD_3_DESC' },
  { value: 'PRICING_METHOD_CD_SRC', label: 'PRICING_METHOD_CD_SRC' },
  { value: 'NOT_COVERED_AMT', label: 'NOT_COVERED_AMT' },
  { value: 'ICD_SRC_VERSION', label: 'ICD_SRC_VERSION' },
  { value: 'PAT_AGE', label: 'PAT_AGE' },
  { value: 'PAT_RELATION', label: 'PAT_RELATION' },
  { value: 'DEID_PAT_BIRTH_DT', label: 'DEID_PAT_BIRTH_DT' },
  { value: 'MBR_ID', label: 'MBR_ID' },
  { value: 'DEID_MBR_ID', label: 'DEID_MBR_ID' },
  { value: 'DEID_MBR_FIRST_NAME', label: 'DEID_MBR_FIRST_NAME' },
  { value: 'MBR_FIRST_NAME', label: 'MBR_FIRST_NAME' },
  { value: 'MBR_LAST_NAME', label: 'MBR_LAST_NAME' },
  { value: 'MBR_BIRTH_DT', label: 'MBR_BIRTH_DT' },
  { value: 'MBR_MIDDLE_NAME', label: 'MBR_MIDDLE_NAME' },
  { value: 'MBR_SSN', label: 'MBR_SSN' },
  { value: 'MBR_HOME_ADDRESS_1', label: 'MBR_HOME_ADDRESS_1' },
  { value: 'MBR_HOME_ADDRESS_2', label: 'MBR_HOME_ADDRESS_2' },
  { value: 'MBR_HOME_CITY', label: 'MBR_HOME_CITY' },
  { value: 'MBR_HOME_STATE', label: 'MBR_HOME_STATE' },
  { value: 'MBR_HOME_ZIP', label: 'MBR_HOME_ZIP' },
  { value: 'SUB_HIRE_DT', label: 'SUB_HIRE_DT' },
  { value: 'SUB_TERMINATION_DT', label: 'SUB_TERMINATION_DT' },
  { value: 'SUB_HOURLY_PAY', label: 'SUB_HOURLY_PAY' },
  { value: 'SUB_ANNUAL_PAY', label: 'SUB_ANNUAL_PAY' },
  { value: 'CLIENT_CD', label: 'CLIENT_CD' },
  { value: 'MBR_GENDER', label: 'MBR_GENDER' },
  { value: 'MBR_RELATION', label: 'MBR_RELATION' },
  { value: 'MBR_RELATION_SRC', label: 'MBR_RELATION_SRC' },
  { value: 'MBR_RELATION_SRC_DESC', label: 'MBR_RELATION_SRC_DESC' },
  { value: 'DEID_MBR_BIRTH_DT', label: 'DEID_MBR_BIRTH_DT' },
  { value: 'SUB_GENDER', label: 'SUB_GENDER' },
  { value: 'SUB_STATUS', label: 'SUB_STATUS' },
  { value: 'SUB_MARITAL_STATUS', label: 'SUB_MARITAL_STATUS' },
  { value: 'PLAN_MBR_STRUCTURE', label: 'PLAN_MBR_STRUCTURE' },
  { value: 'PLAN_TYPE', label: 'PLAN_TYPE' },
  { value: 'COV_START_DT', label: 'COV_START_DT' },
  { value: 'COV_END_DT', label: 'COV_END_DT' },
  { value: 'BENEFIT_LIST', label: 'BENEFIT_LIST' },
  { value: 'MED_IND', label: 'MED_IND' },
  { value: 'DEN_IND', label: 'DEN_IND' },
  { value: 'VIS_IND', label: 'VIS_IND' },
  { value: 'STD_IND', label: 'STD_IND' },
  { value: 'LTD_IND', label: 'LTD_IND' },
];

// Mandatory field mappings that prepopulate for every new config
const MANDATORY_FIELD_MAPPINGS = [
  { source_field: '', destination_field: 'Member ID', classification: 'sensitive', transformations: [] },
  { source_field: '', destination_field: 'Client Person ID', classification: 'sensitive', transformations: [] },
  { source_field: '', destination_field: 'Identifier', classification: 'sensitive', transformations: [] },
  { source_field: '', destination_field: 'Subscriber/policyholder ID', classification: 'sensitive', transformations: [] },
  { source_field: '', destination_field: 'Group/employer identifier', classification: 'sensitive', transformations: [] },
  { source_field: '', destination_field: 'Source person ID', classification: 'sensitive', transformations: [] },
  { source_field: '', destination_field: 'Subscriber Family ID', classification: 'sensitive', transformations: [] },
];

// Supported file format options
const PATTERN_TYPE_OPTIONS = [
  { value: 'txt', label: 'TXT (tab-delimited)', defaultHasHeader: false },
  { value: 'csv', label: 'CSV (comma-separated)', defaultHasHeader: true },
  { value: 'tsv', label: 'TSV (tab-separated)', defaultHasHeader: true },
];

const DEFAULT_CONFIG = {
  pattern_type: 'txt',
  has_header: false,
  field_mappings: MANDATORY_FIELD_MAPPINGS.map(m => ({ ...m })),
};

export default function FieldMappingConfigPage() {
  const { integrationId, revisionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if we're in "new draft" mode (no DB record yet)
  // This becomes false after the first save when currentRevisionId is set
  const [isNewDraftState, setIsNewDraftState] = useState(revisionId === 'new' || location.state?.isNewDraft);

  usePageTitle('Field Mapping Configuration');
  const toast = useToast();

  const [integration, setIntegration] = useState(null);
  const [revisions, setRevisions] = useState([]);
  const [isDeployOpen, setIsDeployOpen] = useState(false);
  const [isDeploying, setIsDeploying] = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [savedConfig, setSavedConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [destinationFields, setDestinationFields] = useState(DEFAULT_DESTINATION_FIELDS);
  const [allowedActions, setAllowedActions] = useState(null);
  const [currentRevisionId, setCurrentRevisionId] = useState(isNewDraftState ? null : revisionId);
  const [fieldValidationErrors, setFieldValidationErrors] = useState({});

  // Use the unsaved changes hook for dirty state tracking and navigation blocking
  const {
    isDirty,
    saveState,
  } = useUnsavedChanges(config, savedConfig, isNewDraftState);

  // Helper to normalize field mappings from backend format to UI format
  // Always ensures the 7 mandatory destination fields are present.
  const normalizeFieldMappings = (mappings) => {
    const normalized = (mappings || [])
      // TODO: If existing configs have tpa_id in mappings, filter it out.
      // In the future, handle tpa_id dynamically based on backend requirements.
      .filter(m => m.destination_field !== 'tpa_id')
      .map(m => {
        // Restore source_field for the UI from whichever key was saved:
        //   - source_field: already a string (header mode or legacy saves)
        //   - source_index: integer saved in no-header mode → convert to string
        //     so the column-index dropdown can match its option values (e.g. "17")
        //   - source_literal: hardcoded value (user-supplied literal)
        let sourceField = '';
        if (m.source_field !== undefined && m.source_field !== null && m.source_field !== '') {
          sourceField = m.source_field;
        } else if (m.source_index !== undefined && m.source_index !== null) {
          sourceField = String(m.source_index);
        } else if (m.source_literal !== undefined && m.source_literal !== null) {
          sourceField = m.source_literal;
        }
        return {
          source_field: sourceField,
          destination_field: m.destination_field || '',
          classification: m.classification || 'non_sensitive',
          transformations: m.transformations || [],
        };
      });

    // Ensure all mandatory destination fields are present
    const existingDestinations = new Set(normalized.map(m => m.destination_field));
    const missingMandatory = MANDATORY_FIELD_MAPPINGS.filter(
      m => !existingDestinations.has(m.destination_field)
    ).map(m => ({ ...m }));

    // Prepend missing mandatory fields so they appear at the top
    return [...missingMandatory, ...normalized];
  };

  // Helper to load LIVE config as starting point
  const loadLiveConfigAsStartingPoint = async (revisionsData) => {
    const liveRevision = revisionsData.find(rev => rev.status === 'LIVE');
    if (!liveRevision) return null;

    try {
      const liveConfig = await fetchRevisionConfig(liveRevision.id);
      if (liveConfig.config_json && Object.keys(liveConfig.config_json).length > 0) {
        const patternType = liveConfig.config_json.pattern_type || 'txt';
        const patternOption = PATTERN_TYPE_OPTIONS.find(o => o.value === patternType);
        const hasHeader = liveConfig.config_json.has_header !== undefined
          ? liveConfig.config_json.has_header
          : (patternOption ? patternOption.defaultHasHeader : false);
        return {
          pattern_type: patternType,
          has_header: hasHeader,
          field_mappings: normalizeFieldMappings(liveConfig.config_json.field_mappings),
        };
      }
    } catch (err) {
      logger.warn('Could not load LIVE config as starting point:', err);
    }
    return null;
  };

  const loadData = useCallback(async (overrideRevisionId = null) => {
    // Use override if provided (for after save when URL hasn't updated yet)
    const effectiveRevisionId = overrideRevisionId || revisionId;
    // Only treat as new draft if effectiveRevisionId is 'new' AND we haven't saved yet
    const isNewDraftMode = effectiveRevisionId === 'new';

    try {
      setLoading(true);
      setError(null);

      // Fetch integration details first
      const integrationResponse = await fetchIntegrationDetails(integrationId);
      const integrationData = integrationResponse.integration || integrationResponse;
      const revisionsData = integrationResponse.revisions || [];

      setIntegration(integrationData);

      // Filter revisions to only show LIVE, DRAFT, and SCHEDULED (not ARCHIVED)
      const filteredRevisions = revisionsData.filter(rev =>
        rev.status === 'LIVE' || rev.status === 'DRAFT' || rev.status === 'SCHEDULED'
      );
      setRevisions(filteredRevisions);

      // Handle new draft mode vs existing revision
      if (isNewDraftMode) {
        // Check if user chose to use LIVE as base (from location.state)
        // On refresh, location.state is lost, so default to empty (start fresh)
        // This ensures unsaved changes are truly lost on refresh
        const useLiveAsBase = location.state?.useLiveAsBase === true;

        if (useLiveAsBase) {
          // Try to load LIVE config as starting point
          const liveConfig = await loadLiveConfigAsStartingPoint(revisionsData);
          if (liveConfig) {
            setConfig(liveConfig);
          } else {
            setConfig({ ...DEFAULT_CONFIG });
          }
        } else {
          setConfig({ ...DEFAULT_CONFIG });
        }
        // New draft: allow save but not deploy until saved
        setAllowedActions({
          can_save: true,
          can_deploy: false,
          reasons: { save: null, deploy: 'Save the configuration first' },
        });
        return;
      }

      // Existing revision: fetch its config
      const configData = await fetchRevisionConfig(effectiveRevisionId);

      // Extract allowed_actions from configData
      if (configData.allowed_actions) {
        setAllowedActions(configData.allowed_actions);
      }

      if (configData.config_json && Object.keys(configData.config_json).length > 0) {
        const patternType = configData.config_json.pattern_type || 'txt';
        // Restore has_header from saved config; fall back to the default for the pattern type
        const patternOption = PATTERN_TYPE_OPTIONS.find(o => o.value === patternType);
        const hasHeader = configData.config_json.has_header !== undefined
          ? configData.config_json.has_header
          : (patternOption ? patternOption.defaultHasHeader : false);
        const loadedConfig = {
          pattern_type: patternType,
          has_header: hasHeader,
          field_mappings: normalizeFieldMappings(configData.config_json.field_mappings),
        };
        setConfig(loadedConfig);
        setSavedConfig(JSON.parse(JSON.stringify(loadedConfig)));
      } else {
        // Draft fallback: if config is empty, try to load LIVE config as starting point
        const liveRevision = revisionsData.find(rev => rev.status === 'LIVE');
        if (liveRevision && liveRevision.id !== parseInt(revisionId)) {
          try {
            const liveConfig = await fetchRevisionConfig(liveRevision.id);
            if (liveConfig.config_json && Object.keys(liveConfig.config_json).length > 0) {
              const patternType = liveConfig.config_json.pattern_type || 'txt';
              const patternOption = PATTERN_TYPE_OPTIONS.find(o => o.value === patternType);
              const hasHeader = liveConfig.config_json.has_header !== undefined
                ? liveConfig.config_json.has_header
                : (patternOption ? patternOption.defaultHasHeader : false);
              setConfig({
                pattern_type: patternType,
                has_header: hasHeader,
                field_mappings: normalizeFieldMappings(liveConfig.config_json.field_mappings),
              });
            } else {
              setConfig({ ...DEFAULT_CONFIG, field_mappings: DEFAULT_CONFIG.field_mappings.map(m => ({ ...m })) });
            }
          } catch (err) {
            logger.warn('Could not load LIVE config as fallback:', err);
            setConfig({ ...DEFAULT_CONFIG, field_mappings: DEFAULT_CONFIG.field_mappings.map(m => ({ ...m })) });
          }
        } else {
          // No LIVE revision to fall back to — use default with mandatory fields
          setConfig({ ...DEFAULT_CONFIG, field_mappings: DEFAULT_CONFIG.field_mappings.map(m => ({ ...m })) });
        }
      }
    } catch (err) {
      logger.error('Error loading data:', err);
      logger.error('Error stack:', err.stack);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrationId, revisionId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // When has_header changes, clear source fields (column names vs indices are incompatible)
  const handleHasHeaderChange = (newHasHeader) => {
    setConfig(prev => ({
      ...prev,
      has_header: newHasHeader,
      field_mappings: prev.field_mappings.map(m => ({ ...m, source_field: '' })),
    }));
  };

  // Client-side validation before save
  const validateConfig = () => {
    const errors = {};
    const mappings = config.field_mappings || [];

    if (mappings.length === 0) {
      return { _form: 'At least one field mapping is required' };
    }

    // Check all 7 required destination fields are mapped
    const requiredDestFields = MANDATORY_FIELD_MAPPINGS.map(m => m.destination_field);
    const mappedDests = new Set(mappings.map(m => m.destination_field).filter(Boolean));
    const missing = requiredDestFields.filter(f => !mappedDests.has(f));
    if (missing.length > 0) {
      return { _form: `All ${requiredDestFields.length} required destination fields must be mapped: ${missing.join(', ')}` };
    }

    // Find duplicate destination fields
    const destSeen = new Map();
    const destDuplicates = new Map();
    mappings.forEach((mapping, index) => {
      const dest = mapping.destination_field;
      if (!dest) return;
      if (destSeen.has(dest)) {
        const firstIndex = destSeen.get(dest);
        destDuplicates.set(index, firstIndex);
        if (!destDuplicates.has(firstIndex)) {
          destDuplicates.set(firstIndex, firstIndex);
        }
      } else {
        destSeen.set(dest, index);
      }
    });

    mappings.forEach((mapping, index) => {
      const rowErrors = {};

      if (!mapping.source_field) {
        rowErrors.source_field = 'Source field is required';
      }
      if (!mapping.destination_field) {
        rowErrors.destination_field = 'Destination field is required';
      } else if (destDuplicates.has(index)) {
        const firstIndex = destDuplicates.get(index);
        rowErrors.destination_field = firstIndex === index
          ? 'Duplicate destination field'
          : `Duplicate destination field (same as row ${firstIndex + 1})`;
      }

      if (Object.keys(rowErrors).length > 0) {
        errors[index] = rowErrors;
      }
    });

    return errors;
  };

  // Internal save function that doesn't reload data (for use during deploy/schedule)
  const saveConfigOnly = async (targetRevisionId) => {
    setError(null);
    // Send pattern_type, has_header, and field_mappings to backend.
    // NO linkage_key, stored_procedure, file_layout, or tpa_id —
    // backend determines those from mapping tables.
    const payload = {
      pattern_type: config.pattern_type,
      has_header: config.has_header,
      field_mappings: config.field_mappings.map(m => ({
        // In no-header mode the user picks a column index from a dropdown;
        // send it as source_index (integer) so the backend ETLConfigBuilder
        // can emit {"map": {"index": N}}.
        // In header mode the user types a column name; send it as source_literal
        // so the backend emits {"map": {"column": "name"}}.
        ...(config.has_header
          ? { source_literal: m.source_field }
          : { source_index: parseInt(m.source_field, 10) }),
        destination_field: m.destination_field,
        classification: m.classification,
        transformations: m.transformations || [],
      })),
    };
    await updateRevisionConfig(targetRevisionId, {
      config_json: payload,
      config_notes: `Updated field mappings - ${new Date().toISOString()}`,
    });
  };

  const handleSaveConfig = async () => {
    // Client-side validation
    const errors = validateConfig();
    setFieldValidationErrors(errors);

    if (errors._form) {
      toast.error(errors._form);
      return;
    }
    if (Object.keys(errors).length > 0) {
      toast.error('Please fix validation errors before saving');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      let targetRevisionId = currentRevisionId;

      // If this is a new draft, create the revision first
      if (isNewDraftState && !currentRevisionId) {
        const newRevision = await createRevision(integrationId);
        targetRevisionId = newRevision.id;
        setCurrentRevisionId(targetRevisionId);
        setIsNewDraftState(false); // No longer a new draft after first save

        // Update URL to reflect the new revision ID (without triggering navigation block)
        window.history.replaceState(
          null,
          '',
          `/integrations/${integrationId}/revisions/${targetRevisionId}/config`
        );
      }

      await saveConfigOnly(targetRevisionId);
      // Update savedConfig to match current config - this clears dirty state
      setSavedConfig(JSON.parse(JSON.stringify(config)));
      setFieldValidationErrors({});
      toast.success('Configuration saved successfully!');

      // Reload data to get updated allowedActions from backend
      // Pass the new revision ID so loadData uses it instead of the URL param
      await loadData(targetRevisionId);
    } catch (err) {
      // Error message is already normalized by api.js
      const errorMessage = err.message || 'Unknown error';
      setError(errorMessage);
      toast.error(`Failed to save configuration: ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  const handleAddMapping = () => {
    setConfig(prev => ({
      ...prev,
      field_mappings: [
        ...prev.field_mappings,
        {
          source_field: '',
          destination_field: '',
          classification: 'non_sensitive',
          transformations: [],
        },
      ],
    }));
  };

  const handleRemoveMapping = (index) => {
    setConfig(prev => ({
      ...prev,
      field_mappings: prev.field_mappings.filter((_, i) => i !== index),
    }));
  };

  const handleMappingChange = (index, field, value) => {
    setConfig(prev => ({
      ...prev,
      field_mappings: prev.field_mappings.map((mapping, i) =>
        i === index ? { ...mapping, [field]: value } : mapping
      ),
    }));
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const text = await file.text();
      const lines = text.split('\n');
      if (lines.length > 0) {
        const headers = lines[0].split(',').map(h => h.trim()).filter(Boolean);

        // Build an expanded destination fields list by adding title-cased
        // versions of any CSV headers that don't already exist
        const expandedFields = [...DEFAULT_DESTINATION_FIELDS];
        const existingLower = new Set(expandedFields.map(f => f.value.toLowerCase()));
        headers.forEach(header => {
          const titleCased = toTitleCase(header);
          if (!existingLower.has(titleCased.toLowerCase())) {
            expandedFields.push({ value: titleCased, label: titleCased });
            existingLower.add(titleCased.toLowerCase());
          }
        });
        setDestinationFields(expandedFields);

        // Auto-generate field mappings from CSV headers,
        // skipping any whose destination already exists in current mappings
        setConfig(prev => {
          const existingDestinations = new Set(
            prev.field_mappings.map(m => m.destination_field?.toLowerCase()).filter(Boolean)
          );

          const newMappings = headers
            .filter(header => !existingDestinations.has(toTitleCase(header).toLowerCase()))
            .map(header => ({
              source_field: '',
              destination_field: toTitleCase(header),
              classification: 'non_sensitive',
              transformations: [],
            }));

          return {
            ...prev,
            field_mappings: [...prev.field_mappings, ...newMappings],
          };
        });
      }
    } catch (err) {
      setError('Failed to read file');
    }
  };

  const handleDeploy = async () => {
    try {
      setIsDeploying(true);
      // Save config without reloading data (which would cause a flash)
      await saveConfigOnly(currentRevisionId);
      await deployRevision(currentRevisionId);
      toast.success('Configuration deployed successfully!');
      // Navigate to client's integration details page to see updated status
      navigate(`/integrations/${integration.client}`);
    } catch (err) {
      toast.error(`Deployment failed: ${err.message}`);
    } finally {
      setIsDeploying(false);
    }
  };

  const handleScheduleDeploy = async (scheduledAt) => {
    try {
      setIsScheduling(true);
      // Save config without reloading data (which would cause a flash)
      await saveConfigOnly(currentRevisionId);
      await scheduleDeployment(integrationId, scheduledAt);
      toast.success('Deployment scheduled successfully!');
      // Navigate to client's integration details page to see updated status
      // The route is /integrations/:clientId where clientId is the client's ID
      navigate(`/integrations/${integration.client}`);
    } catch (err) {
      toast.error(`Scheduling failed: ${err.message}`);
    } finally {
      setIsScheduling(false);
    }
  };

  const handleDeployModalConfirm = async (payload) => {
    // Don't close modal yet - wait for operations to complete
    // This prevents the flash of the config page before navigation
    if (payload.mode === 'now') {
      await handleDeploy();
      setIsDeployOpen(false); // Close after deploy completes
    } else {
      await handleScheduleDeploy(payload.scheduledAt);
      // Don't close here since handleScheduleDeploy navigates away
    }
  };

  const handleCancelSchedule = async () => {
    try {
      setIsDeploying(true);
      await cancelScheduledDeployment(integrationId);
      toast.success('Scheduled deployment cancelled!');
      await loadData();
    } catch (err) {
      toast.error(`Failed to cancel: ${err.message}`);
    } finally {
      setIsDeploying(false);
    }
  };

  // Track current revision status
  const currentRevision = revisions.find(r => r.id === parseInt(revisionId));
  const hasScheduledDeployment = currentRevision?.status === 'SCHEDULED';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error && !integration) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-destructive">Error: {error}</p>
      </div>
    );
  }

  if (!integration) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-4 lg:p-6 space-y-4 lg:space-y-6">
      {/* Page Header */}
      <Card>
        <CardContent className="p-4 sm:p-5 lg:p-6">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-start gap-3">
                <button
                  onClick={() => navigate(`/integrations/${integration.client}`)}
                  className="mt-1 inline-flex items-center justify-center rounded-md h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                  title="Back to integration details"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold">Field Mapping Configuration</h1>
                  <p className="text-sm text-muted-foreground">
                    {integration.client_name || 'Unknown'} / {integration.display_name || 'Unknown'}
                    {isDirty && !isNewDraftState && <span className="ml-2 text-amber-600">(Unsaved Changes)</span>}
                  </p>
                </div>
              </div>
              <div className="flex gap-2 items-center">
                <select
                  value={isNewDraftState ? 'new' : (currentRevisionId || revisionId)}
                  onChange={(e) => navigate(`/integrations/${integrationId}/revisions/${e.target.value}/config`)}
                  className="h-9 rounded-md border border-input bg-white px-3 py-2 text-sm"
                >
                  {isNewDraftState && (
                    <option value="new">New Draft (Unsaved)</option>
                  )}
                  {revisions.map(rev => (
                    <option key={rev.id} value={rev.id}>
                      Version {rev.revision_number} ({rev.status})
                    </option>
                  ))}
                </select>
                <Button variant="outline" size="sm">
                  <Upload className="mr-2 h-4 w-4" />
                  Run Test
                </Button>
                <Tooltip content={!saveState.canSave ? (saveState.tooltipMessage || allowedActions?.reasons?.save) : null}>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSaveConfig}
                    disabled={!saveState.canSave || saving}
                  >
                    {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Save Draft
                  </Button>
                </Tooltip>
                {hasScheduledDeployment && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCancelSchedule}
                    disabled={isDeploying}
                  >
                    Cancel Schedule
                  </Button>
                )}
                <Tooltip content={allowedActions?.reasons?.deploy}>
                  <Button
                    size="sm"
                    onClick={() => setIsDeployOpen(true)}
                    disabled={!allowedActions?.can_deploy || isDeploying || isScheduling || saving}
                  >
                    {isDeploying ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Deploying...
                      </>
                    ) : isScheduling ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Scheduling...
                      </>
                    ) : (
                      'Deploy Configuration'
                    )}
                  </Button>
                </Tooltip>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Source Section */}
      <Card>
        <CardContent className="p-4 sm:p-5 lg:p-6">
          <h2 className="text-lg font-semibold mb-4">Data Source</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Client / Carrier</label>
              <input
                type="text"
                value={`${integration.client_name || 'Unknown'} / ${integration.carrier?.name || 'N/A'}`}
                disabled
                className="mt-1 flex h-10 w-full rounded-md border border-input bg-muted px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Content Type</label>
              <input
                type="text"
                value={(integration.content_type || '').replace(/_/g, ' ')}
                disabled
                className="mt-1 flex h-10 w-full rounded-md border border-input bg-muted px-3 py-2 text-sm"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Field Mappings Section */}
      <Card>
        <CardContent className="p-4 sm:p-5 lg:p-6">
          <div className="mb-4 text-center">
            <h2 className="text-lg font-semibold">Field Mappings</h2>
            <p className="text-sm text-muted-foreground mt-0.5">Define how source data maps to destination fields.</p>
            {/* Header/No Header Toggle — centered below the heading */}
            <div className="flex items-center justify-center gap-4 mt-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="has_header"
                  value="true"
                  checked={config.has_header === true}
                  onChange={() => handleHasHeaderChange(true)}
                  className="h-4 w-4 text-blue-600"
                />
                <span className="text-sm">Header</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="has_header"
                  value="false"
                  checked={config.has_header === false}
                  onChange={() => handleHasHeaderChange(false)}
                  className="h-4 w-4 text-blue-600"
                />
                <span className="text-sm">No Header</span>
              </label>
              {config.has_header && (
                <label className="ml-2 text-blue-600 cursor-pointer hover:underline text-sm">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  Import from CSV
                </label>
              )}
            </div>
          </div>

          {fieldValidationErrors._form && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{fieldValidationErrors._form}</p>
            </div>
          )}

          <div className="border rounded-lg overflow-hidden">
            <div className="bg-gray-50 grid grid-cols-12 gap-2 p-3 text-sm font-medium border-b">
              <div className="col-span-3">Source Field</div>
              <div className="col-span-3">Maps To</div>
              <div className="col-span-2">Classification</div>
              <div className="col-span-3">Transformations</div>
              <div className="col-span-1"></div>
            </div>

            <div className="divide-y">
              {(!config.field_mappings || config.field_mappings.length === 0) ? (
                <div className="p-8 text-center text-muted-foreground">
                  No field mappings defined. Click &quot;Add Field Mapping&quot; to get started.
                </div>
              ) : (
                config.field_mappings.map((mapping, index) => {
                  const rowErrors = fieldValidationErrors[index] || {};
                  const isMandatory = MANDATORY_FIELD_MAPPINGS.some(
                    m => m.destination_field === mapping.destination_field
                  );

                  return (
                    <div key={index} className="grid grid-cols-12 gap-2 p-3 items-start">
                      {/* Source Field — text input (has_header) or column index dropdown (no header) */}
                      <div className="col-span-3">
                        {config.has_header ? (
                          <input
                            type="text"
                            value={mapping.source_field}
                            onChange={(e) => handleMappingChange(index, 'source_field', e.target.value)}
                            placeholder="Enter column header name..."
                            className={`w-full h-10 rounded-md border bg-background px-3 py-2 text-sm ${
                              rowErrors.source_field
                                ? 'border-red-500 focus:ring-red-500'
                                : 'border-input'
                            }`}
                          />
                        ) : (
                          <select
                            value={mapping.source_field}
                            onChange={(e) => handleMappingChange(index, 'source_field', e.target.value)}
                            className={`w-full h-10 rounded-md border bg-background px-3 py-2 text-sm ${
                              rowErrors.source_field
                                ? 'border-red-500 focus:ring-red-500'
                                : 'border-input'
                            }`}
                          >
                            <option value="">Select column...</option>
                            {Array.from({ length: 100 }, (_, i) => (
                              <option key={i} value={String(i)}>Col {i}</option>
                            ))}
                          </select>
                        )}
                        {rowErrors.source_field && (
                          <p className="text-xs text-red-500 mt-1">{rowErrors.source_field}</p>
                        )}
                      </div>

                      {/* Maps To (Destination Field) */}
                      <div className="col-span-3">
                        <select
                          value={mapping.destination_field}
                          onChange={(e) => handleMappingChange(index, 'destination_field', e.target.value)}
                          className={`w-full h-10 rounded-md border bg-background px-3 py-2 text-sm ${
                            rowErrors.destination_field
                              ? 'border-red-500 focus:ring-red-500'
                              : 'border-input'
                          }`}
                        >
                          <option value="">Select destination...</option>
                          {destinationFields.map(dest => (
                            <option key={dest.value} value={dest.value}>{dest.label}</option>
                          ))}
                        </select>
                        {rowErrors.destination_field && (
                          <p className="text-xs text-red-500 mt-1">{rowErrors.destination_field}</p>
                        )}
                      </div>

                      {/* Classification */}
                      <div className="col-span-2">
                        <select
                          value={mapping.classification}
                          onChange={(e) => handleMappingChange(index, 'classification', e.target.value)}
                          className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                        >
                          {CLASSIFICATIONS.map(cls => (
                            <option key={cls.value} value={cls.value}>{cls.label}</option>
                          ))}
                        </select>
                      </div>

                      {/* Transformations */}
                      <div className="col-span-3">
                        <TransformationSelector
                          transformations={mapping.transformations || []}
                          onChange={(newTransformations) =>
                            handleMappingChange(index, 'transformations', newTransformations)
                          }
                        />
                      </div>

                      {/* Delete Button */}
                      <div className="col-span-1 flex justify-end">
                        <button
                          onClick={() => handleRemoveMapping(index)}
                          disabled={isMandatory}
                          className={isMandatory
                            ? 'text-muted-foreground/30 cursor-not-allowed'
                            : 'text-muted-foreground hover:text-destructive'}
                          title={isMandatory ? 'Required field cannot be removed' : 'Remove mapping'}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* TODO: Tooltips for input type, client_person_id, sub_id, grp_id_src (TBD) */}
          <div className="mt-4">
            <Button variant="outline" onClick={handleAddMapping}>
              <Plus className="mr-2 h-4 w-4" />
              Add Field Mapping
            </Button>
          </div>
        </CardContent>
      </Card>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <DeployConfigurationModal
        isOpen={isDeployOpen}
        onClose={() => setIsDeployOpen(false)}
        onConfirm={handleDeployModalConfirm}
        isLoading={isDeploying || isScheduling}
        loadingMode={isDeploying ? 'deploying' : isScheduling ? 'scheduling' : null}
      />
    </div>
  );
}
